import { Injectable } from '@angular/core';
import { UpdatePointsTransferMembershipData } from '@features/membership/services/membership-api/membership-api.service';
import {
  ButtonTheme,
  LoyaltyProgram,
  SuccessButton,
  SuccessButtonGroup,
  TranslationKeyType,
} from '@rd/types';

export type CheckoutLinkedAccountPosition = 'top' | 'bottom' | 'outside'; // relative to the checkout form. example: top of the form or outside of the form
export type LoyaltyProgramNameAttributes = 'shortName' | 'name';
export type CheckoutSubmitButtonPosition = 'inside' | 'outside'; // relative to the checkout form.

type checkoutPageText = 'additionalCheckoutTitle' | 'additionalCheckoutMessage';
export type TcV2CheckoutPageTextMap = Record<
  checkoutPageText,
  TranslationKeyType | null
>;

@Injectable()
export class TransferConnectV2SettingService {
  loyaltyProgramNameAttribute: LoyaltyProgramNameAttributes = 'name';

  /*
  ====================================
    Listing page-only settings
  ====================================
  */

  listItemDesktopButtonTheme: ButtonTheme = 'primary';
  showListingFilters = false;
  showCategoryFilter = true;
  shouldRenderListingAlert = false;

  /*
  ====================================
    Checkout page-only settings
  ====================================
  */

  autoRedeemDateFormat = 'd MMMM YYYY';
  checkoutPageText: TcV2CheckoutPageTextMap = {
    additionalCheckoutTitle: null,
    additionalCheckoutMessage: null,
  };
  collapsibleTransferAmountForm = false;
  linkAccountDialogMobileCloseIcon = 'close';
  linkedAccountPosition: CheckoutLinkedAccountPosition = 'bottom';
  shouldDisablePrefilledLinkAccountFields = false;
  shouldPrefillLinkAccountForm = false;
  shouldRenderCheckoutBanner = false;
  shouldRenderCheckoutBreadcrumb = true;
  shouldRenderEnrollmentLinkInCheckoutForm = true;
  shouldRenderLinkAccountButtonIcon = false;
  shouldRenderLinkAccountFormDisclaimer = true;
  submitButtonPosition: CheckoutSubmitButtonPosition = 'inside';
  hasPromocode = false;
  shouldShowDobFieldInLinkForm: (id: string) => boolean = () => false;
  backToCheckoutPageUrl: (loyaltyProgramId: string) => {
    url: string;
    queryParams?: { [key: string]: string };
  } = (loyaltyProgramId: string) => {
    return {
      url: `/transfer/checkout/${loyaltyProgramId}`,
    };
  };

  checkoutTransferAmountFormSourceCurrencyNameAttribute: LoyaltyProgramNameAttributes =
    'name';
  insufficientMilesCurrencyNameAttribute: LoyaltyProgramNameAttributes =
    'shortName';
  linkAccountDisclaimerLoyaltyProgramNameAttribute: LoyaltyProgramNameAttributes =
    'shortName';

  getLoyaltyProgramDisplayName(
    updateMembershipData: UpdatePointsTransferMembershipData,
  ): string {
    return updateMembershipData.loyaltyProgramShortName ?? '';
  }

  /*
  ====================================
    Review page-only settings
  ====================================
  */

  shouldRenderReviewTnc = false;

  formattedDestinationAmountName(loyaltyProgram: LoyaltyProgram): string {
    return loyaltyProgram?.loyaltyCurrency?.name ?? '';
  }

  /*
  ====================================
    Success page-only settings
  ====================================
  */

  // eslint-disable-next-line @typescript-eslint/member-ordering
  targetLoyaltyCurrencyNameAttribute: Record<
    LoyaltyProgram['transferConnectIdentifier'],
    LoyaltyProgramNameAttributes
  > = {};

  successButtons(): SuccessButtonGroup {
    const makeAnotherRedemptionButton: SuccessButton = {
      buttonClass: 'full-width-on-mobile flips-on-mobile',
      buttonText: 'transfer_v2.success.make_another_redemption',
      buttonTheme: 'primary',
      redirectURL: '/',
    };

    const toPointsTransactionButton: SuccessButton = {
      buttonClass: 'full-width-on-mobile flips-on-mobile',
      buttonText: 'transfer_v2.success.see_points_transaction',
      buttonTheme: 'secondary',
      redirectURL: '/points_activity',
    };

    return {
      confirmed: [toPointsTransactionButton, makeAnotherRedemptionButton],
      pending: [
        { ...makeAnotherRedemptionButton, buttonTheme: 'secondary' },
        { ...toPointsTransactionButton, buttonTheme: 'primary' },
      ],
      failed: [
        { ...makeAnotherRedemptionButton, buttonTheme: 'secondary' },
        { ...toPointsTransactionButton, buttonTheme: 'primary' },
      ],
    };
  }
}
